import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import { dateFormat } from "../../utils";
import { useNavigate } from "react-router-dom";
import DriverListModal from "../ModalContent/DriverListModal";
import Swal from "../../messageBox/customSweetAlert";
import { DELETE_SCHEDULE, DELETE_MULTIPLE_SCHEDULES } from "../../constant";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/loaderAction";
import axios from "axios";

const PRIMARY_COLOR = "#F4A442";
const SECONDARY_COLOR = "#2D5A50";

const SERVICE_COLUMN_STYLE = {
  width: "250px",
  maxWidth: "250px",
  minWidth: "250px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function formatLegDriverName(driver) {
  if (!driver) return "";
  const parts = driver.fullName? [driver.fullName] : [driver.firstName, driver.middleName, driver.lastName].filter(
    Boolean,
  );
  if (parts.length) return parts.join(" ").replace(/\s+/g, " ").trim();
  return driver.fullName || "";
}

/** First leg’s `schedule_trans_id` (same ordering as row driver display). */
function getFirstScheduleTransId(details) {
  const sorted = Array.isArray(details.transData)
    ? [...details.transData].sort(
      (a, b) => (a?.schedule_trans_id ?? 0) - (b?.schedule_trans_id ?? 0),
    )
    : [];
  return sorted[0]?.schedule_trans_id ?? null;
}

function ScheduleTableV1({
  tableData,
  setTableData,
  diverList,
  setSelectedDriverId,
  selectedDriverId,
  onSave,
  setSelectedScheduleId,
  setSelectedScheduleTransId,
  setModalVisible,
  isModalVisible,
  isClicked,
  pagination,
  onPageChange,
  onRefreshAfterDelete,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState([]);

  const handleCheckboxChange = (scheduleId) => {
    setCheckedItems((prev) => {
      const isChecked = prev.includes(scheduleId);
      if (isChecked) {
        return prev.filter((i) => i !== scheduleId);
      }
      return [...prev, scheduleId];
    });
  };

  const handleSelectAll = () => {
    const currentPageIds = tableData.map((data) => data.schedule_id);
    const allSelected =
      currentPageIds.length > 0 &&
      currentPageIds.every((id) => checkedItems.includes(id));

    if (allSelected) {
      setCheckedItems((prev) =>
        prev.filter((id) => !currentPageIds.includes(id)),
      );
    } else {
      setCheckedItems((prev) => {
        const newItems = [...prev];
        currentPageIds.forEach((id) => {
          if (!newItems.includes(id)) {
            newItems.push(id);
          }
        });
        return newItems;
      });
    }
  };

  const areAllSelected =
    tableData.length > 0 &&
    tableData.every((data) => checkedItems.includes(data.schedule_id));

  const areSomeSelected =
    tableData.some((data) => checkedItems.includes(data.schedule_id)) &&
    !areAllSelected;

  const handleEditClick = (id) => {
    navigate(`/home/schedule/edit/${id}`);
  };

  const openModal = (scheduleId, scheduleTransId) => {
    setModalVisible(true);
    setSelectedScheduleId(scheduleId);
    setSelectedScheduleTransId(scheduleTransId);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const handleDeleteClick = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this item!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(setLoading(true));
        axios
          .delete(`${DELETE_SCHEDULE}/${id}`)
          .then((response) => {
            const status = response?.data?.status;
            const ok = status === 200 || status === "200";
            if (ok) {
              dispatch(setLoading(false));
              Swal.fire({
                title: "Success!",
                text:
                  response?.data?.message ||
                  "Schedule deleted successfully",
                icon: "success",
                confirmButtonText: "OK",
              });
              setCheckedItems((prev) => prev.filter((sid) => sid !== id));
              if (onRefreshAfterDelete) {
                onRefreshAfterDelete();
              } else {
                const updatedTableData = tableData.filter(
                  (item) => item.schedule_id !== id,
                );
                setTableData(updatedTableData);
              }
            } else if (status === 400 || status === "400") {
              dispatch(setLoading(false));
              Swal.fire({
                title: "",
                text: response.data.message,
                icon: "Error",
                confirmButtonText: "OK",
              });
            } else {
              dispatch(setLoading(false));
              Swal.fire({
                title: "Failed",
                text: response?.data?.message || "Could not delete schedule.",
                icon: "error",
                confirmButtonText: "OK",
              });
            }
          })
          .catch((err) => {
            dispatch(setLoading(false));
            Swal.fire({
              title: "Error",
              text:
                err?.response?.data?.message ||
                err?.message ||
                "Could not delete schedule.",
              icon: "error",
              confirmButtonText: "OK",
            });
          });
      }
    });
  };

  const handleDeleteMultiple = () => {
    if (!checkedItems.length) return;
    const idsToDelete = [...checkedItems];
    Swal.fire({
      title: "Are you sure?",
      text: `Delete ${idsToDelete.length} selected schedule(s)? You will not be able to recover them!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete them!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(setLoading(true));
        axios
          .delete(DELETE_MULTIPLE_SCHEDULES, {
            data: { ids: idsToDelete },
            headers: { "Content-Type": "application/json" },
          })
          .then((response) => {
            const status = response?.data?.status;
            const ok = status === 200 || status === "200";
            if (ok) {
              dispatch(setLoading(false));
              Swal.fire({
                title: "Success!",
                text:
                  response?.data?.message ||
                  "Schedules deleted successfully",
                icon: "success",
                confirmButtonText: "OK",
              });
              setCheckedItems([]);
              if (onRefreshAfterDelete) {
                onRefreshAfterDelete();
              } else {
                setTableData((prev) =>
                  prev.filter(
                    (item) => !idsToDelete.includes(item.schedule_id),
                  ),
                );
              }
            } else if (status === 400 || status === "400") {
              dispatch(setLoading(false));
              Swal.fire({
                title: "",
                text: response.data.message,
                icon: "Error",
                confirmButtonText: "OK",
              });
            } else {
              dispatch(setLoading(false));
              Swal.fire({
                title: "Failed",
                text:
                  response?.data?.message ||
                  "Could not delete schedules.",
                icon: "error",
                confirmButtonText: "OK",
              });
            }
          })
          .catch((err) => {
            dispatch(setLoading(false));
            Swal.fire({
              title: "Error",
              text:
                err?.response?.data?.message ||
                err?.message ||
                "Could not delete schedules.",
              icon: "error",
              confirmButtonText: "OK",
            });
          });
      }
    });
  };

  const myArray = {};
  tableData.forEach((val) => {
    const schid = val.schedule_id;
    if (!myArray[schid]) {
      myArray[schid] = [];
    }
    const sortedTransData = Array.isArray(val.transData)
      ? [...val.transData].sort(
        (a, b) => (a?.schedule_trans_id ?? 0) - (b?.schedule_trans_id ?? 0),
      )
      : val.transData;
    myArray[schid].push(sortedTransData);
  });

  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.currentPage ?? 1;
  const totalRecords = pagination?.totalRecords ?? tableData.length;
  const recordsPerPage = pagination?.recordsPerPage ?? 10;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(
    startIndex + (tableData?.length || 0),
    totalRecords,
  );

  const getPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "ellipsis", totalPages];
  };

  const pageNumbers = getPageNumbers();
  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <button
          type="button"
          className="btn btn-sm"
          style={{
            backgroundColor: "#dc3545",
            color: "#fff",
            border: "none",
          }}
          disabled={checkedItems.length === 0}
          onClick={handleDeleteMultiple}
        >
          Delete Multiple
        </button>
      </div>
      <Table striped bordered hover className="tbl-require">
        <thead>
          <tr>
            <th style={{ width: 44, textAlign: "center" }}>
              <input
                type="checkbox"
                checked={areAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = areSomeSelected;
                }}
                onChange={handleSelectAll}
                style={{ cursor: "pointer" }}
                title={areAllSelected ? "Deselect all" : "Select all"}
              />
            </th>
            <th>Trip Date</th>
            <th>Schedule Type</th>
            <th>Passenger</th>
            <th>Payor</th>
            {/* <th>No of Leg</th> */}
            <th title="Appointment Time">Appt Time</th>
            <th style={SERVICE_COLUMN_STYLE}>Service</th>
            <th>Pickup / Drop off Details</th>
            {/* <th>Bill Amount</th> */}
            <th style={{ textAlign: "center" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {tableData && tableData.length > 0 ? (
            tableData.map((details) => {
              const legs = myArray[details.schedule_id];

              return (
                <tr key={details.schedule_id}>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    <input
                      type="checkbox"
                      value={details.schedule_id}
                      checked={checkedItems.includes(details.schedule_id)}
                      onChange={() =>
                        handleCheckboxChange(details.schedule_id)
                      }
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                  <td>{dateFormat(details?.schedule_date) ?? ""}</td>
                  <td>
                    {details?.is_upload_schedule === "0"
                      ? "Manual"
                      : "Spreadsheet"}
                  </td>
                  <td>
                    {details.passenger
                      ? `${details.passenger?.firstName ?? ""} ${details.passenger?.middleName ?? ""} ${details.passenger?.lastName ?? ""}-${details.passenger?.passenger?.pass_pmi_no ?? ""}`
                        .replace(/\s+/g, " ")
                        .trim()
                      : ""}
                  </td>
                  <td>
                    {details.passenger?.passenger?.payerDetails?.payer_name}
                  </td>
                  {/* <td>{details.no_of_leg}</td> */}
                  <td>{details?.appt_time ?? "N/A"}</td>
                  <td
                    style={SERVICE_COLUMN_STYLE}
                    title={
                      details?.nemtServiceRate?.serviceDetails?.service_name ?? ""
                    }
                  >
                    {details?.nemtServiceRate?.serviceDetails?.service_name ?? (
                      <span style={{ color: "red", fontWeight: "lighter" }}>
                        Please add the Service
                      </span>
                    )}
                  </td>
                  <td>
                    {legs && legs.length > 0 ? (
                      legs.map((legArray, legArrayIndex) => (
                        <div key={legArrayIndex}>
                          {legArray.map((leg, legIndex) => {
                            const driverName = formatLegDriverName(leg?.driver);
                            const isLastLeg = legIndex === legArray.length - 1;
                            return (
                              <div
                                key={legIndex}
                                style={{
                                  paddingBottom: "10px",
                                  marginBottom: isLastLeg ? "0" : "12px",
                                  borderBottom: isLastLeg
                                    ? "none"
                                    : "1px solid #cfd8dc",
                                }}
                              >
                                <div style={{ paddingBottom: "8px" }}>
                                  <span
                                    style={{
                                      marginTop: "5px",
                                      fontWeight: "bold",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {`Trip ${legIndex + 1} (Ride Id: ${leg?.ride_id ?? "N/A"})`}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    color: "#105c64",
                                    fontStyle: "italic",
                                    fontWeight: "lighter",
                                  }}
                                >
                                  Driver:{" "}
                                </span>
                                {driverName ? (
                                  <span>{driverName}</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn  btn-sm"
                                    style={{
                                      backgroundColor: "#31876F",
                                      color: "#fff",
                                      padding: "0px 7px",
                                      marginLeft: "5px",
                                    }}
                                    onClick={() =>
                                      openModal(
                                        details.schedule_id,
                                        leg.schedule_trans_id,
                                      )
                                    }
                                  >
                                    Assign Driver
                                  </button>
                                )}
                                <br />
                                <div></div>
                                <div style={{ paddingTop: "5px" }}>
                                  <div style={{ paddingBottom: "5px" }}>
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                      }}
                                    >
                                      Pickup Location:
                                    </span>
                                    {leg.pickupLocation?.pickup_location &&
                                      `${leg.pickupLocation.pickup_location.replace(/,,/, "")}, `}
                                    {leg.pickupLocation?.pickup_address &&
                                      `${leg.pickupLocation.pickup_address.replace(/,,/, "")}, `}
                                    {leg.pickupLocation?.pickup_city &&
                                      `${leg.pickupLocation.pickup_city.replace(/,,/, "")}, `}
                                    <br />
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                      }}
                                    >
                                      Pickup Time:
                                    </span>{" "}
                                    {leg.pick_up_time}
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      Location Type:
                                    </span>{" "}
                                    {
                                      leg.pickupLocation?.locationType
                                        ?.location_type_name
                                    }
                                    <br />
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                      }}
                                    >
                                     Scheduled Miles:
                                    </span>{" "}
                                    {leg?.miles ?? "N/A"}
                                    <br />
                                  </div>
                                  {/* <div>&nbsp;</div> */}
                                  <div>
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                      }}
                                    >
                                      Drop Off Location:
                                    </span>
                                    {leg.dropoffLocation?.dropoff_location &&
                                      `${leg.dropoffLocation?.dropoff_location.replace(/,,/, "")}, `}
                                    {leg.dropoffLocation?.dropoff_address &&
                                      `${leg.dropoffLocation?.dropoff_address.replace(/,,/, "")}, `}
                                    {leg.dropoffLocation?.dropoff_city &&
                                      `${leg.dropoffLocation?.dropoff_city.replace(/,,/, "")}, `}
                                    <br />
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                      }}
                                    >
                                      Drop Off Time:
                                    </span>{" "}
                                    {leg.drop_off_time}
                                    <span
                                      style={{
                                        color: "#105c64",
                                        fontStyle: "italic",
                                        fontWeight: "lighter",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      Location Type:
                                    </span>{" "}
                                    {
                                      leg.dropoffLocation?.locationType
                                        ?.location_type_name
                                    }
                                    <br />

                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      <div>No legs available</div>
                    )}
                  </td>
                  {/* <td>
                    {details?.bill_amount ? `$${details?.bill_amount}` : ""}
                  </td> */}
                  <td style={{ textAlign: "center", cursor: "pointer" }}>
                    <i
                      className="pi pi-pen-to-square icon-btn-edit"
                      onClick={() => handleEditClick(details.schedule_id)}
                    />
                    <i
                      className="pi pi-trash icon-btn-delete"
                      onClick={() => handleDeleteClick(details.schedule_id)}
                    />
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={10} style={{ textAlign: "center" }}>
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div
          className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 px-4 py-3 mt-0"
          style={{
            backgroundColor: "rgba(248, 249, 250, 0.8)",
            border: "1px solid #e9ecef",
            borderTop: "none",
            borderRadius: "0 0 0.25rem 0.25rem",
          }}
        >
          <p className="text-sm text-muted mb-0">
            Showing{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {tableData?.length ? startIndex + 1 : 0}-{endIndex}
            </span>{" "}
            of{" "}
            <span style={{ color: SECONDARY_COLOR, fontWeight: 700 }}>
              {totalRecords}
            </span>{" "}
            results
          </p>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: currentPage === 1 ? 0.35 : 1 }}
              disabled={currentPage === 1}
              onClick={() => onPageChange?.(1)}
              title="First page"
              aria-label="First page"
            >
              <i className="pi pi-angle-double-left" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none me-1"
              style={{ opacity: currentPage === 1 ? 0.35 : 1 }}
              disabled={currentPage === 1}
              onClick={() => onPageChange?.(currentPage - 1)}
              title="Previous page"
              aria-label="Previous page"
            >
              <i className="pi pi-angle-left" />
            </button>
            <div className="d-flex align-items-center gap-1">
              {pageNumbers.map((p, i) =>
                p === "ellipsis" ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-muted">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className="btn btn-sm fw-semibold rounded"
                    style={{
                      minWidth: 32,
                      height: 32,
                      backgroundColor:
                        p === currentPage ? PRIMARY_COLOR : "transparent",
                      color: p === currentPage ? "#fff" : "#495057",
                      border: "none",
                      boxShadow:
                        p === currentPage
                          ? "0 1px 2px rgba(0,0,0,0.05)"
                          : "none",
                    }}
                    onClick={() => onPageChange?.(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === currentPage ? "page" : undefined}
                  >
                    {p}
                  </button>
                ),
              )}
            </div>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none ms-1"
              style={{ opacity: currentPage === totalPages ? 0.35 : 1 }}
              disabled={currentPage === totalPages}
              onClick={() => onPageChange?.(currentPage + 1)}
              title="Next page"
              aria-label="Next page"
            >
              <i className="pi pi-angle-right" />
            </button>
            <button
              type="button"
              className="btn btn-link p-2 text-secondary border-0 shadow-none"
              style={{ opacity: currentPage === totalPages ? 0.35 : 1 }}
              disabled={currentPage === totalPages}
              onClick={() => onPageChange?.(totalPages)}
              title="Last page"
              aria-label="Last page"
            >
              <i className="pi pi-angle-double-right" />
            </button>
          </div>
        </div>
      )}

      <DriverListModal
        visible={isModalVisible}
        onHide={closeModal}
        title="Drivers List"
        driverList={diverList}
        setSelectedDriverId={setSelectedDriverId}
        selectedDriverId={selectedDriverId}
        onSave={onSave}
        isClicked={isClicked}
      />
    </>
  );
}

export default ScheduleTableV1;
